import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, Mail, Download, Eye, RefreshCw, Search, ExternalLink,
  Filter, Users, CheckCircle2, Clock,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import FreeToolFunnelWidget from '../components/FreeToolFunnelWidget';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FOUNDER_EMAIL = 'demo@cartieai.com';

/**
 * AdminLeads — founder-only follow-up dashboard for lead-magnet subscribers.
 *
 * Shows every email captured via the cornerstone PDFs with quick filters
 * (which magnet · downloaded yet?) and a "Send personal email" button
 * that opens Gmail compose with a tailored template per cornerstone.
 *
 * Goal: turn subscribers into customer #1. The fastest path is one
 * personal email from the founder within 24h of subscribing.
 */

const _MAGNET_LABEL = {
  'ec2-right-sizing-checklist': 'EC2 Right-Sizing',
  'lambda-traps-checklist': 'Lambda Cost Traps',
  'redshift-snowflake-worksheet': 'Redshift vs Snowflake',
};

const _MAGNET_TONE = {
  'ec2-right-sizing-checklist': 'bg-orange-500/15 text-orange-200 border-orange-400/30',
  'lambda-traps-checklist': 'bg-yellow-500/15 text-yellow-200 border-yellow-400/30',
  'redshift-snowflake-worksheet': 'bg-rose-500/15 text-rose-200 border-rose-400/30',
};

// Per-magnet email templates — friendly, founder-voice, no sales pitch.
const _EMAIL_TEMPLATE = {
  'ec2-right-sizing-checklist': {
    subject: 'Saw you grabbed the EC2 right-sizing checklist 👋',
    body: `Hey — Lakshmi from CARTIEAI here.

Saw you downloaded the EC2 right-sizing checklist a couple of days ago. Hope it's already saved you some headaches!

Quick question: what triggered you to grab it? A surprise bill? A planned audit? Genuinely curious — we built this because every founder we've talked to has the same EC2 over-provisioning story.

If you'd like, I'd love to take a quick look at your top 5 EC2 instances and tell you exactly where the savings hide. Free, takes me 30 minutes — and you can use whatever I find with or without our tool.

Reply with a yes/no, and we'll pick a time.

— Lakshmi
Founder, CARTIEAI
hello@cartieai.com`,
  },
  'lambda-traps-checklist': {
    subject: 'Saw you grabbed the Lambda cost-traps checklist 👋',
    body: `Hey — Lakshmi from CARTIEAI here.

Noticed you downloaded the Lambda cost-traps checklist. The provisioned-concurrency one is the trap that surprises almost everyone.

Quick question: which trap did you spot first when you went through it?

If you'd like, I can run our Lambda audit against your account in 30 minutes — read-only, no commitment. Most teams find $3-25K/month in waste on the first scan.

Worth a look?

— Lakshmi
Founder, CARTIEAI
hello@cartieai.com`,
  },
  'redshift-snowflake-worksheet': {
    subject: 'Saw you grabbed the Redshift vs Snowflake worksheet 👋',
    body: `Hey — Lakshmi from CARTIEAI here.

Saw you downloaded the Redshift vs Snowflake decision worksheet. Big call — both vendors will tell you their version of "you're picking us".

Quick question: where did you land on the 5-signal score? And which gotcha (Managed Storage on Redshift, Cloud Services on Snowflake) bit you?

If you'd like, I can model your migration cost across 12 months for both options. Same data, two scenarios, totally honest. Takes 45 minutes.

Reply if useful — happy to send a quick scheduling link.

— Lakshmi
Founder, CARTIEAI
hello@cartieai.com`,
  },
};

const _gmailComposeUrl = (to, subject, body) => {
  const params = new URLSearchParams({
    view: 'cm',
    fs: '1',
    to,
    su: subject,
    body,
  });
  return `https://mail.google.com/mail/?${params.toString()}`;
};

const AdminLeads = () => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [magnetFilter, setMagnetFilter] = useState('all');
  const [downloadFilter, setDownloadFilter] = useState('all'); // all | downloaded | not_downloaded
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/lead-magnets/admin/recent?limit=200`, {
        headers: { 'X-Founder-Email': FOUNDER_EMAIL },
      });
      setRows(r.data?.recent || []);
      setCounts(r.data?.by_magnet || {});
    } catch (e) {
      toast.error('Could not load subscribers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (magnetFilter !== 'all' && r.magnet_slug !== magnetFilter) return false;
    if (downloadFilter === 'downloaded' && !r.downloaded_at) return false;
    if (downloadFilter === 'not_downloaded' && r.downloaded_at) return false;
    if (search && !r.email?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleEmail = (sub) => {
    const tmpl = _EMAIL_TEMPLATE[sub.magnet_slug] || {
      subject: 'Hey from CARTIEAI 👋',
      body: 'Hey — Lakshmi from CARTIEAI here. Saw you signed up for one of our checklists. How can we help?\n\n— Lakshmi',
    };
    const url = _gmailComposeUrl(sub.email, tmpl.subject, tmpl.body);
    // Best-effort: bump email-sent counter on the subscriber so the
    // /admin/conversion-funnel page can show "personal email sent" stage.
    // Fires fire-and-forget; the Gmail compose tab opens regardless.
    if (sub.token) {
      axios.post(
        `${API}/lead-magnets/admin/email-tracked/${sub.token}`,
        {},
        { headers: { 'X-Founder-Email': FOUNDER_EMAIL } },
      ).catch(() => { /* tracking is non-critical */ });
    }
    window.open(url, '_blank', 'noopener');
    // Optimistically reflect the email in this page's row counter too.
    setRows((prev) =>
      prev.map((r) =>
        r.token === sub.token
          ? { ...r, last_emailed_at: new Date().toISOString(), email_sent_count: (r.email_sent_count || 0) + 1 }
          : r,
      ),
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-slate-900 px-6 py-12 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-300 mr-2" />
        Loading subscribers…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-slate-900 px-4 sm:px-6 py-12" data-testid="admin-leads-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-2"><BrandTitle>Lead Subscribers</BrandTitle></h1>
            <p className="text-sm text-slate-400 max-w-2xl">
              Every email captured via the cornerstone PDFs. The fastest path to
              customer #1 is one personal email within 24h. Click <b>Send personal email</b> to open
              Gmail compose with a tailored template.
            </p>
          </div>
          <Button
            data-testid="leads-refresh-btn"
            onClick={load}
            variant="outline"
            className="bg-slate-900 hover:bg-slate-800 border-slate-700 text-slate-200"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Free-tool funnel — broader-context view above the cornerstone PDFs */}
        <FreeToolFunnelWidget />

        {/* Magnet roll-up */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          {Object.entries(_MAGNET_LABEL).map(([slug, label]) => {
            const c = counts[slug] || { subscribers: 0, downloads: 0 };
            return (
              <Card
                key={slug}
                data-testid={`leads-magnet-card-${slug}`}
                className={`border ${
                  magnetFilter === slug
                    ? 'bg-emerald-500/10 border-emerald-400/40'
                    : 'bg-slate-900 text-slate-100 border-slate-800 hover:border-slate-700'
                } cursor-pointer transition-colors`}
                onClick={() => setMagnetFilter(magnetFilter === slug ? 'all' : slug)}
              >
                <CardContent className="p-4">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-slate-400 mb-1">
                    {label}
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-2xl font-bold text-white tabular-nums">{c.subscribers}</span>
                    <span className="text-[11px] text-slate-400">subscribers</span>
                    <span className="text-slate-400 mx-1">·</span>
                    <span className="text-base font-semibold text-cyan-300 tabular-nums">{c.downloads}</span>
                    <span className="text-[11px] text-slate-400">downloads</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="leads-search-input"
              type="text"
              placeholder="Search email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-slate-900 border-slate-700 text-white"
            />
          </div>
          <div className="flex items-center gap-1.5 text-[11px]">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <button
              data-testid="leads-filter-all"
              onClick={() => setDownloadFilter('all')}
              className={`px-2.5 py-1.5 rounded-md border ${
                downloadFilter === 'all'
                  ? 'bg-slate-700 text-white border-slate-600'
                  : 'bg-slate-900 text-slate-400 border-slate-800 hover:bg-slate-800'
              }`}
            >
              All
            </button>
            <button
              data-testid="leads-filter-downloaded"
              onClick={() => setDownloadFilter('downloaded')}
              className={`px-2.5 py-1.5 rounded-md border ${
                downloadFilter === 'downloaded'
                  ? 'bg-emerald-500/20 text-emerald-200 border-emerald-400/40'
                  : 'bg-slate-900 text-slate-400 border-slate-800 hover:bg-slate-800'
              }`}
            >
              Downloaded
            </button>
            <button
              data-testid="leads-filter-not-downloaded"
              onClick={() => setDownloadFilter('not_downloaded')}
              className={`px-2.5 py-1.5 rounded-md border ${
                downloadFilter === 'not_downloaded'
                  ? 'bg-amber-500/20 text-amber-200 border-amber-400/40'
                  : 'bg-slate-900 text-slate-400 border-slate-800 hover:bg-slate-800'
              }`}
            >
              Not yet
            </button>
          </div>
        </div>

        {/* Subscriber rows */}
        <Card className="bg-slate-900 text-slate-100 border-slate-800">
          <CardContent className="p-0">
            {filtered.length === 0 ? (
              <p className="p-8 text-sm text-slate-400 text-center italic">
                {rows.length === 0
                  ? 'No subscribers yet — share the cornerstone posts to start capturing leads.'
                  : 'No subscribers match the current filters.'}
              </p>
            ) : (
              <div className="divide-y divide-slate-800/60">
                <div className="px-4 py-2.5 grid grid-cols-12 gap-3 text-[11px] uppercase tracking-widest font-bold text-slate-400 bg-slate-950">
                  <div className="col-span-4">Email</div>
                  <div className="col-span-3">Magnet</div>
                  <div className="col-span-2">Subscribed</div>
                  <div className="col-span-1 text-center">DL</div>
                  <div className="col-span-2 text-right">Action</div>
                </div>
                {filtered.map((s) => (
                  <div
                    key={s.id || s.token}
                    data-testid={`leads-row-${s.email_hash || s.email}`}
                    className="px-4 py-3 grid grid-cols-12 gap-3 items-center hover:bg-slate-950 transition-colors"
                  >
                    <div className="col-span-4 min-w-0">
                      <div className="font-medium text-white text-sm truncate" title={s.email}>
                        {s.email}
                      </div>
                      {s.referer && (
                        <div className="text-[11px] text-slate-400 truncate flex items-center gap-1" title={s.referer}>
                          <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                          {s.referer.replace(/^https?:\/\//, '').slice(0, 42)}
                        </div>
                      )}
                    </div>
                    <div className="col-span-3">
                      <Badge className={`text-[11px] tracking-wider border ${_MAGNET_TONE[s.magnet_slug] || 'bg-slate-700/50 text-slate-300 border-slate-600/50'}`}>
                        {_MAGNET_LABEL[s.magnet_slug] || s.magnet_slug}
                      </Badge>
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center gap-1 text-[11px] text-slate-400">
                        <Clock className="w-3 h-3" />
                        {s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString() : '—'}
                      </div>
                    </div>
                    <div className="col-span-1 text-center">
                      {s.downloaded_at ? (
                        <CheckCircle2
                          className="w-4 h-4 text-emerald-300 inline"
                          title={`Downloaded ${new Date(s.downloaded_at).toLocaleString()}`}
                        />
                      ) : (
                        <span
                          className="text-slate-400 text-[11px]"
                          title="Subscribed but never downloaded"
                        >
                          —
                        </span>
                      )}
                    </div>
                    <div className="col-span-2 flex justify-end">
                      <Button
                        data-testid={`leads-email-btn-${s.email_hash || s.email}`}
                        onClick={() => handleEmail(s)}
                        size="sm"
                        className="h-8 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold border-0 text-[11px]"
                      >
                        <Mail className="w-3 h-3 mr-1" />
                        Email
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="mt-6 text-[11px] text-slate-400 text-center">
          Founder-private · Click a magnet card to filter · Personal email opens Gmail compose with a tailored template.
        </p>
      </div>
    </div>
  );
};

export default AdminLeads;
